/* eslint-disable @typescript-eslint/no-explicit-any */
import { dispatchToast, getCSRFToken, type ToastType } from "./htmx-helpers";

/**
 * Alpine component for drag-drop reordering of deliverables on the project
 * sidebar. The template (templates/components/project_sidebar_right.templ)
 * declares `x-data="deliverableReorder({ projectID: '...' })"` and wires
 * `@dragstart`, `@dragover.prevent`, `@drop.prevent`, `@dragend` on each
 * `[data-deliverable-id]` row inside the `[data-deliverable-list]` container.
 *
 * The DOM is reordered live in `onDragOver` (so the user sees the slot move
 * under their cursor); `onDrop` then PUTs the new order to the server. We
 * read the resulting `flash-message` HX-Trigger event manually because raw
 * `fetch` bypasses htmx's auto-trigger handling.
 */
export const registerDeliverableReorder = (Alpine: any) => {
  Alpine.data("deliverableReorder", ({ projectID }: { projectID: string }) => ({
    projectID,
    draggingEl: null as HTMLElement | null,

    // NOTE: native HTML5 drag (not SortableJS) + immediate fetch-persist, so
    // it does NOT use applySortableReorder (that util fixes SortableJS's
    // pre-onEnd DOM move for Alpine keyed x-for — a problem this file doesn't
    // have). Keep the two mechanisms separate.

    onDragStart(event: DragEvent, el: HTMLElement) {
      this.draggingEl = el;
      el.classList.add("opacity-50");
      // Firefox requires dataTransfer to have data set or it won't fire drag events.
      event.dataTransfer?.setData("text/plain", el.dataset.deliverableId || "");
      if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
    },

    onDragOver(event: DragEvent, el: HTMLElement) {
      if (!this.draggingEl || this.draggingEl === el) return;
      const list = el.parentElement;
      if (!list) return;
      const rect = el.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      if (event.clientY < midpoint) {
        list.insertBefore(this.draggingEl, el);
      } else {
        list.insertBefore(this.draggingEl, el.nextSibling);
      }
    },

    onDrop(_event: DragEvent, _el: HTMLElement) {
      // The actual DOM reorder happened in dragover; just persist.
      this.persist();
    },

    onDragEnd(_el: HTMLElement) {
      if (this.draggingEl) {
        this.draggingEl.classList.remove("opacity-50");
        this.draggingEl = null;
      }
    },

    async persist() {
      const root = (this as any).$root as HTMLElement;
      const list = root.querySelector("[data-deliverable-list]") as HTMLElement | null;
      if (!list) return;
      const ids = Array.from(list.querySelectorAll("[data-deliverable-id]")).map((el) => ({
        id: (el as HTMLElement).dataset.deliverableId,
      }));
      try {
        const res = await fetch(`/api/projects/${this.projectID}/deliverable-order`, {
          method: "PUT",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            // CSRF required by server middleware. Without it the PUT 403s.
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify({ order: ids }),
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        // Server emits {"flash-message": {"msg": "...", "type": "success"}}
        // via HX-Trigger on success. htmx auto-handles that for
        // htmx-initiated requests, but raw fetch bypasses that, so we
        // parse the header and dispatch the toast event ourselves.
        const trigger = res.headers.get("HX-Trigger");
        if (trigger) {
          try {
            const payload = JSON.parse(trigger);
            const toast = payload["flash-message"];
            if (toast && toast.msg) {
              dispatchToast(toast.msg, (toast.type as ToastType) || "success");
            }
          } catch {
            /* ignore malformed trigger */
          }
        }
      } catch (err) {
        console.error("[deliverable-reorder] persist failed", err);
        // Optimistic UI: a page reload will re-fetch the canonical order.
      }
    },
  }));
};
